/**
 * pages/_demo/TemplateDemoPage.jsx — foundation smoke-test page.
 *
 * Demonstrates the canonical Self-Serve module pattern:
 *   <MainLayout> + <PageHeader> + <DataTable> + <EmptyState>
 * All API calls go through `api/_template.js`. Field-level encryption
 * round-trips via the Reveal action.
 *
 * This page is reachable at /admin/_template-demo (route registered in
 * App.js below). It's kept as a live demo on purpose — when adding a
 * new module, fork this file as a starting point.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Eye, FileBox } from 'lucide-react';

import MainLayout from '../../components/MainLayout';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { DataTable } from '../../components/data-table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../../components/ui/dialog';
import {
  listTemplateItems, createTemplateItem,
  getTemplateItemFull, deleteTemplateItem,
} from '../../api/_template';
import { getCurrentAccount } from '../../lib/auth';

export default function TemplateDemoPage() {
  const account = getCurrentAccount();
  const accountId = account?.id;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', secret: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState(null);

  const limit = 25;

  const load = async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const data = await listTemplateItems({ accountId, limit, offset });
      setRows(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error('Failed to load items', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId, offset]);

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setCreating(true);
    try {
      await createTemplateItem({ accountId, name: form.name, secret: form.secret || null });
      toast.success('Item created');
      setForm({ name: '', secret: '' });
      setCreateOpen(false);
      setOffset(0);
      load();
    } catch (e) {
      toast.error('Failed to create', { description: e?.response?.data?.detail || e.message });
    } finally {
      setCreating(false);
    }
  };

  const handleReveal = async (row) => {
    try {
      const full = await getTemplateItemFull({ accountId, id: row.id });
      setRevealed({ id: row.id, name: row.name, secret: full.secret });
    } catch (e) {
      toast.error('Failed to decrypt', { description: e?.response?.data?.detail || e.message });
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await deleteTemplateItem({ accountId, id: row.id });
      toast.success('Item deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete', { description: e?.response?.data?.detail || e.message });
    }
  };

  const columns = [
    { key: 'name',   header: 'Name',       accessor: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'secret', header: 'Secret',     accessor: (r) => r.secret_last4 ? <code className="text-xs">••••{r.secret_last4}</code> : <span className="text-muted-foreground text-xs">—</span> },
    { key: 'created', header: 'Created',   accessor: (r) => <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>,
      cellClassName: 'whitespace-nowrap' },
  ];

  return (
    <MainLayout activePage="_template-demo" pageLabel="Template Demo" accountId={accountId}>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <PageHeader
          title="Foundation Demo"
          description="Self-Serve Module 1 reference — encrypted field round-trip, tenant-isolated CRUD, shared DataTable / PageHeader / EmptyState primitives."
          actions={
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid="template-demo-new-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> New Item
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>New encrypted item</DialogTitle>
                  <DialogDescription>
                    The <code>secret</code> field is encrypted at rest with AES-256-GCM under your org's key — only the last 4 characters are stored in plaintext for list previews.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Name *</label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. test-record"
                      data-testid="template-demo-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Secret (encrypted at rest)</label>
                    <Input
                      type="password"
                      value={form.secret}
                      onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))}
                      placeholder="anything sensitive"
                      data-testid="template-demo-secret-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="template-demo-cancel-btn">Cancel</Button>
                  <Button onClick={handleCreate} disabled={creating || !form.name.trim()} data-testid="template-demo-save-btn">
                    {creating ? 'Saving…' : 'Save'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          }
        />

        {!accountId ? (
          <EmptyState
            icon={FileBox}
            title="No account selected"
            description="Choose an organisation in the top-bar to view encrypted items."
            testId="template-demo-no-account"
          />
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            total={total}
            offset={offset}
            limit={limit}
            loading={loading}
            onPageChange={setOffset}
            exportFilename={`template-items-${accountId}.csv`}
            testId="template-demo-table"
            emptyTitle="No items yet"
            emptyHint="Create your first encrypted item — its plaintext stays in memory only."
            emptyAction={
              <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="template-demo-empty-cta">
                <Plus className="w-4 h-4 mr-1.5" /> New Item
              </Button>
            }
            rowActions={(r) => (
              <div className="flex justify-end gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleReveal(r)}
                  data-testid={`template-demo-reveal-${r.id}`}
                  title="Decrypt secret"
                >
                  <Eye className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(r)}
                  data-testid={`template-demo-delete-${r.id}`}
                  title="Delete item"
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            )}
          />
        )}

        <Dialog open={!!revealed} onOpenChange={(o) => !o && setRevealed(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{revealed?.name}</DialogTitle>
              <DialogDescription>
                Decrypted plaintext — this read was recorded to the audit log.
              </DialogDescription>
            </DialogHeader>
            <div className="font-mono text-sm break-all bg-muted p-3 rounded" data-testid="template-demo-revealed-secret">
              {revealed?.secret || <span className="text-muted-foreground italic">no secret set</span>}
            </div>
            <p className="text-xs text-muted-foreground">
              The decryption was logged to <code>security_audit_log</code> with action{' '}
              <code>_template.read_full</code>.
            </p>
            <DialogFooter>
              <Button onClick={() => setRevealed(null)} data-testid="template-demo-revealed-close">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
